import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileUpload } from '@/components/ui/file-upload';
import { useStories } from '@/hooks/useStories';
import { Upload, Image, Video } from 'lucide-react';

interface CreateStoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStoryCreated?: () => Promise<void>;
}

export const CreateStoryDialog = ({ open, onOpenChange, onStoryCreated }: CreateStoryDialogProps) => {
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState('86400'); // 24 hours
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { createStory } = useStories();

  const handleFileSelect = (files: FileList | null) => {
    if (files && files[0]) {
      const selectedFile = files[0];
      
      // Check file size (100MB limit)
      const maxSize = 100 * 1024 * 1024; // 100MB in bytes
      if (selectedFile.size > maxSize) {
        alert('Ֆայլի չափը չպետք է գերազանցի 100 ՄԲ-ը');
        return;
      }

      // Check file type
      if (!selectedFile.type.match(/^(image|video)\//)) {
        alert('Խնդրում ենք ընտրել միայն ֆոտո կամ վիդեո ֆայլ');
        return;
      }

      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!file) return;

    setIsUploading(true);
    try {
      await createStory(file, title, parseInt(duration));
      onOpenChange(false);
      setTitle('');
      setFile(null);
      setDuration('86400');
      
      // Call the callback to refresh the stories list
      if (onStoryCreated) {
        await onStoryCreated();
      }
    } catch (error) {
      console.error('Error creating story:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const durationOptions = [
    { value: '3600', label: '1 ժամ' },
    { value: '21600', label: '6 ժամ' },
    { value: '43200', label: '12 ժամ' },
    { value: '86400', label: '24 ժամ' },
    { value: '172800', label: '2 օր' },
    { value: '604800', label: '1 շաբաթ' }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload size={20} />
            Նոր պատմություն
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="story-file">Ֆայլ</Label>
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
              {file ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-center">
                    {file.type.startsWith('image/') ? (
                      <Image className="w-8 h-8 text-green-500" />
                    ) : (
                      <Video className="w-8 h-8 text-blue-500" />
                    )}
                  </div>
                  <p className="text-sm font-medium">{file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setFile(null)}
                  >
                    Փոխել
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="w-8 h-8 text-muted-foreground mx-auto" />
                  <div>
                    <Input
                      id="story-file"
                      type="file"
                      accept="image/*,video/*"
                      onChange={(e) => handleFileSelect(e.target.files)}
                      className="hidden"
                    />
                    <Label
                      htmlFor="story-file"
                      className="cursor-pointer text-sm text-primary hover:underline"
                    >
                      Ընտրել ֆայլ
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Ֆոտո կամ վիդեո (առավելագույնը 100 ՄԲ)
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Վերնագիր (ոչ պարտադիր)</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ավելացրեք վերնագիր..."
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="duration">Տևողություն</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {durationOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Չեղարկել
            </Button>
            <Button
              type="submit"
              disabled={!file || isUploading}
              className="flex-1"
            >
              {isUploading ? 'Վերբեռնում...' : 'Հրապարակել'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};